/**
 * Primitivas ESC/POS en MODO TEXTO (spec 053): comandos, codificación CP850,
 * ajuste de línea y columnas, QR nativo y bloque raster opcional.
 *
 * Funciones PURAS (sin DOM). La fuente interna de la impresora (Font A,
 * 12×24) da 48 columnas en 80 mm (576 px) y 32 en 58 mm (384 px).
 */
import { encodeEscposRaster } from "../encodeEscposRaster";
import type { PaperWidthMm, RasterImage } from "../types";

/** Columnas de texto por ancho de papel (Font A). */
export const COLUMNS_BY_MM: Record<PaperWidthMm, number> = { 80: 48, 58: 32 };

/** Tamaño de módulo del QR nativo por ancho de papel. */
export const QR_MODULE_BY_MM: Record<PaperWidthMm, number> = { 80: 6, 58: 4 };

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

export const CMD = {
  /** ESC @ — reinicia la impresora. */
  init: [ESC, 0x40],
  /** ESC t 2 — tabla de caracteres PC850 (Multilingual Latin I). */
  codePagePc850: [ESC, 0x74, 0x02],
  /** ESC a n — 0 izquierda, 1 centro, 2 derecha. */
  align: (n: 0 | 1 | 2) => [ESC, 0x61, n],
  /** ESC E n — negrita. */
  bold: (on: boolean) => [ESC, 0x45, on ? 1 : 0],
  /** GS ! n — tamaño: 0x00 normal, 0x10 doble ancho, 0x01 doble alto, 0x11 ambos. */
  size: (n: number) => [GS, 0x21, n & 0xff],
  /** GS V 66 0 — corte con avance (igual que el raster). */
  cut: [GS, 0x56, 0x42, 0x00],
};

export const SIZE_NORMAL = 0x00;
export const SIZE_DOUBLE_HEIGHT = 0x01;

// ─── CP850 ──────────────────────────────────────────────────────────────────

/** Mitad alta de CP850 (bytes 0x80–0xFF), en orden. */
export const CP850_HIGH =
  "ÇüéâäàåçêëèïîìÄÅ" +
  "ÉæÆôöòûùÿÖÜø£Ø×ƒ" +
  "áíóúñÑªº¿®¬½¼¡«»" +
  "░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐" +
  "└┴┬├─┼ãÃ╚╔╩╦╠═╬¤" +
  "ðÐÊËÈıÍÎÏ┘┌█▄¦Ì▀" +
  "ÓßÔÒõÕµþÞÚÛÙýÝ¯´" +
  "­±‗¾¶§÷¸°¨·¹³²■ ";

const CP850_MAP: Map<string, number> = (() => {
  const m = new Map<string, number>();
  for (let i = 0; i < CP850_HIGH.length; i++) m.set(CP850_HIGH[i], 0x80 + i);
  // El espacio duro se imprime como espacio normal.
  m.delete(" ");
  m.delete("­");
  return m;
})();

/** Sustituciones de caracteres sin equivalente en CP850. */
const REPLACEMENTS: Record<string, string> = {
  "→": "->",
  "←": "<-",
  "…": "...",
  "€": "EUR",
  "™": "TM",
  "−": "-", // signo menos
  "–": "-", // en dash
  "—": "-", // em dash
  "‐": "-",
  "‑": "-",
  "‘": "'",
  "’": "'",
  "‚": "'",
  "“": '"',
  "”": '"',
  "„": '"',
  "•": "*",
  " ": " ",
  " ": " ",
  " ": " ",
  " ": " ",
  "​": "",
  "­": "",
  "\t": " ",
  "\r": "",
};

const isPrintableAscii = (code: number) => code >= 0x20 && code < 0x7f;

/**
 * Normaliza un texto para que CADA carácter resultante ocupe exactamente un
 * byte en CP850 (así el ancho en columnas = length). Lo que no existe en la
 * tabla cae a su equivalente sin tilde, y si no hay, a "?".
 */
export function toPrinterText(input: unknown): string {
  const text = input == null ? "" : String(input);
  let out = "";
  for (const ch of text) {
    if (ch === "\n") {
      out += ch;
      continue;
    }
    const code = ch.codePointAt(0) ?? 0;
    if (isPrintableAscii(code) || CP850_MAP.has(ch)) {
      out += ch;
      continue;
    }
    if (ch in REPLACEMENTS) {
      out += REPLACEMENTS[ch];
      continue;
    }
    // Sin tilde (NFD + quitar diacríticos) si así queda imprimible.
    const stripped = ch.normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (
      stripped &&
      [...stripped].every(
        (c) => isPrintableAscii(c.codePointAt(0) ?? 0) || CP850_MAP.has(c)
      )
    ) {
      out += stripped;
      continue;
    }
    if (code < 0x20) continue; // controles: fuera
    out += "?";
  }
  return out;
}

/** Codifica texto a bytes CP850 (aplica `toPrinterText` antes). */
export function encodeCp850(input: unknown): number[] {
  const text = toPrinterText(input);
  const bytes: number[] = [];
  for (const ch of text) {
    if (ch === "\n") {
      bytes.push(LF);
      continue;
    }
    const code = ch.codePointAt(0) ?? 0x3f;
    bytes.push(code < 0x80 ? code : CP850_MAP.get(ch) ?? 0x3f);
  }
  return bytes;
}

// ─── Texto: ajuste de línea y columnas ──────────────────────────────────────

/**
 * Ajusta un texto (ya normalizado o no) a `width` columnas: corta por
 * palabras; una palabra más larga que el ancho (p. ej. el hash) se parte.
 */
export function wrapText(input: unknown, width: number): string[] {
  const text = toPrinterText(input);
  const w = Math.max(1, width);
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    const words = paragraph.split(" ").filter((x) => x.length > 0);
    if (words.length === 0) {
      lines.push("");
      continue;
    }
    let current = "";
    for (let word of words) {
      while (word.length > w) {
        if (current) {
          lines.push(current);
          current = "";
        }
        lines.push(word.slice(0, w));
        word = word.slice(w);
      }
      if (!word) continue;
      if (!current) current = word;
      else if (current.length + 1 + word.length <= w) current += " " + word;
      else {
        lines.push(current);
        current = word;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

export const padRight = (s: string, width: number) =>
  s.length >= width ? s : s + " ".repeat(width - s.length);

export const padLeft = (s: string, width: number) =>
  s.length >= width ? s : " ".repeat(width - s.length) + s;

/** Línea separadora de ancho completo. */
export const separator = (width: number, char = "-") => char.repeat(width);

export interface Column {
  text: string;
  width: number;
  align?: "left" | "right";
}

/**
 * Une columnas de ancho fijo en una sola línea. El texto de cada columna NO se
 * trunca (un monto jamás se corta): quien llama verifica que quepa.
 */
export function columns(cols: Column[]): string {
  return cols
    .map((c) => {
      const t = toPrinterText(c.text);
      return c.align === "right" ? padLeft(t, c.width) : padRight(t, c.width);
    })
    .join("");
}

// ─── QR nativo ──────────────────────────────────────────────────────────────

/** Capacidad máxima del QR modelo 2 en modo byte con corrección M (v40). */
const QR_MAX_BYTES = 2331;

/**
 * QR nativo `GS ( k` (modelo 2, módulo `moduleSize`, corrección M): selecciona
 * modelo, tamaño, corrección, guarda los datos e imprime.
 */
export function qrCommand(data: string, moduleSize: number): number[] {
  const payload = encodeCp850(data);
  if (payload.length === 0) return [];
  if (payload.length > QR_MAX_BYTES) {
    throw new Error("qrCommand: datos demasiado largos para el QR");
  }
  const size = Math.min(16, Math.max(1, Math.round(moduleSize)));
  const storeLen = payload.length + 3;
  return [
    // Modelo 2: GS ( k 4 0 49 65 50 0
    GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00,
    // Tamaño de módulo: GS ( k 3 0 49 67 n
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, size,
    // Corrección M: GS ( k 3 0 49 69 49
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31,
    // Guardar datos: GS ( k pL pH 49 80 48 d1..dk
    GS, 0x28, 0x6b, storeLen & 0xff, (storeLen >> 8) & 0xff, 0x31, 0x50, 0x30,
    ...payload,
    // Imprimir: GS ( k 3 0 49 81 48
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30,
  ];
}

/**
 * Bloque de imagen `GS v 0` (p. ej. un logo) reutilizando el codificador del
 * raster, SIN su `ESC @` inicial (reiniciaría la tabla de caracteres) ni corte.
 */
export function rasterBlock(img: RasterImage): number[] {
  const bytes = encodeEscposRaster(img, { feedLines: 0, cut: false });
  const start = bytes[0] === ESC && bytes[1] === 0x40 ? 2 : 0;
  return Array.from(bytes.subarray(start));
}

// ─── Escritor ───────────────────────────────────────────────────────────────

/** Acumulador de bytes ESC/POS con helpers de texto. */
export class EscposTextWriter {
  private readonly bytes: number[] = [];

  constructor(readonly width: number) {}

  raw(cmd: number[]): this {
    for (const b of cmd) this.bytes.push(b);
    return this;
  }

  align(a: "left" | "center" | "right"): this {
    return this.raw(CMD.align(a === "left" ? 0 : a === "center" ? 1 : 2));
  }

  bold(on: boolean): this {
    return this.raw(CMD.bold(on));
  }

  size(n: number): this {
    return this.raw(CMD.size(n));
  }

  /** Escribe texto SIN salto de línea. */
  text(s: unknown): this {
    return this.raw(encodeCp850(s));
  }

  /** Escribe una línea (texto + LF). */
  line(s: unknown = ""): this {
    return this.text(s).raw([LF]);
  }

  /** Escribe un texto ajustado al ancho, una línea por renglón. */
  wrapped(s: unknown, width = this.width): this {
    for (const l of wrapText(s, width)) this.line(l);
    return this;
  }

  /** «ETIQUETA: valor» con la etiqueta en negrita, ajustado al ancho. */
  labeled(label: string, value: unknown): this {
    const lbl = toPrinterText(label);
    const lines = wrapText(`${lbl} ${toPrinterText(value)}`.trim(), this.width);
    lines.forEach((l, i) => {
      if (i === 0 && l.startsWith(lbl)) {
        this.bold(true).text(lbl).bold(false).line(l.slice(lbl.length));
      } else {
        this.line(l);
      }
    });
    return this;
  }

  separator(char = "-"): this {
    return this.line(separator(this.width, char));
  }

  feed(n = 1): this {
    for (let i = 0; i < n; i++) this.bytes.push(LF);
    return this;
  }

  qr(data: string, moduleSize: number): this {
    return this.raw(qrCommand(data, moduleSize));
  }

  image(img: RasterImage): this {
    return this.raw(rasterBlock(img));
  }

  cut(): this {
    return this.raw(CMD.cut);
  }

  toUint8Array(): Uint8Array {
    return Uint8Array.from(this.bytes);
  }
}
